import { useCallback, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  PageContainer,
  ErrorBoundary,
  Select,
  Button,
  DataTable,
  Skeleton,
  EmptyState,
  Modal,
  Input,
  RadioGroup,
  DatePicker,
  Drawer,
  ConfirmDialog,
  useToast,
} from '@bbpos/ui';
import type { ColumnDef } from '@bbpos/ui';
import {
  useCampaigns,
  useCampaign,
  useCreateCampaign,
  useSendCampaign,
  useDeleteCampaign,
  useTemplates,
  StatusBadge,
  ChannelPill,
} from '@/features/communications';
import { useCommunicationStore } from '@/stores/use-communication-store';
import type {
  Campaign,
  CampaignAudienceType,
  CampaignStatus,
  CreateCampaignInput,
} from '@/features/communications';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'DRAFT', label: 'Draft' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'QUEUED', label: 'Queued' },
  { value: 'SENDING', label: 'Sending' },
  { value: 'COMPLETED', label: 'Completed' },
  { value: 'PARTIAL', label: 'Partly sent' },
  { value: 'FAILED', label: 'Failed' },
];

const AUDIENCE_OPTIONS = [
  { value: 'ALL', label: 'All Customers' },
  { value: 'RFM_SEGMENT', label: 'RFM Segment' },
  { value: 'CUSTOM', label: 'Custom' },
];

const RFM_SEGMENTS = [
  { value: 'CHAMPION', label: 'Champions' },
  { value: 'LOYAL', label: 'Loyal' },
  { value: 'POTENTIAL_LOYALIST', label: 'Potential Loyalists' },
  { value: 'AT_RISK', label: 'At Risk' },
  { value: 'INACTIVE', label: 'Inactive' },
];

const CHANNEL_OPTIONS = [
  { value: 'WHATSAPP', label: 'WhatsApp' },
  { value: 'EMAIL', label: 'Email' },
  { value: 'SMS', label: 'SMS' },
];

type Row = Campaign;

const QUEUED_NOTE = 'Messages are going out to each customer. Sent and Failed update as they go.';

// A campaign can be sent now only while it is still a DRAFT or SCHEDULED.
const canSend = (s: CampaignStatus) => s === 'DRAFT' || s === 'SCHEDULED';

function CampaignsContent() {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const createBtnRef = useRef<HTMLButtonElement | null>(null);

  const page = Number(searchParams.get('page') ?? '1');
  const status = (searchParams.get('status') ?? '') as CampaignStatus | '';

  const { data, isLoading, isFetching } = useCampaigns({ page, status });
  const rows = useMemo<Row[]>(() => data?.data ?? [], [data]);

  // Detail drawer
  const { selectedCampaignId, setSelectedCampaignId } = useCommunicationStore();
  const { data: selectedCampaign } = useCampaign(selectedCampaignId ?? undefined);

  const [showCreate, setShowCreate] = useState(false);
  const [campaignName, setCampaignName] = useState('');
  const [audienceType, setAudienceType] = useState<CampaignAudienceType>('ALL');
  const [rfmSegment, setRfmSegment] = useState('CHAMPION');
  const [selectedChannels, setSelectedChannels] = useState<string[]>(['WHATSAPP']);
  const [templateId, setTemplateId] = useState('');
  const [scheduledAt, setScheduledAt] = useState<Date | undefined>(undefined);
  const [deleteTarget, setDeleteTarget] = useState<Campaign | null>(null);
  const [sendTarget, setSendTarget] = useState<Campaign | null>(null);

  const { data: templatesData } = useTemplates({ page: 1 });
  const templateOptions = useMemo(
    () => [
      { value: '', label: 'No template' },
      ...(templatesData?.data ?? []).map((t) => ({ value: t.id, label: t.name })),
    ],
    [templatesData],
  );

  const createMutation = useCreateCampaign();
  const sendMutation = useSendCampaign();
  const deleteMutation = useDeleteCampaign();

  function setParam(key: string, value: string) {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    if (key !== 'page') params.set('page', '1');
    setSearchParams(params);
  }

  function toggleChannel(ch: string) {
    setSelectedChannels((prev) =>
      prev.includes(ch) ? prev.filter((c) => c !== ch) : [...prev, ch],
    );
  }

  function closeCreate() {
    setShowCreate(false);
    createBtnRef.current?.focus();
  }

  const handleCreate = useCallback(() => {
    if (!campaignName.trim()) {
      toast({ variant: 'warning', title: 'Name required' });
      return;
    }
    if (selectedChannels.length === 0) {
      toast({ variant: 'warning', title: 'Select at least one channel' });
      return;
    }

    const input: CreateCampaignInput = {
      name: campaignName.trim(),
      template_id: templateId || undefined,
      audience_type: audienceType,
      audience_filter: audienceType === 'RFM_SEGMENT' ? { segment: rfmSegment } : undefined,
      channels: selectedChannels,
      scheduled_at: scheduledAt ? scheduledAt.toISOString() : undefined,
    };

    const sendNow = !scheduledAt;
    createMutation.mutate(input, {
      onSuccess: (campaign) => {
        closeCreate();
        setCampaignName('');
        setAudienceType('ALL');
        setSelectedChannels(['WHATSAPP']);
        setTemplateId('');
        setScheduledAt(undefined);

        // "Leave empty to send immediately" — with no schedule, dispatch the
        // freshly-created DRAFT right away so it actually goes out.
        if (sendNow && campaign?.id) {
          sendMutation.mutate(campaign.id, {
            onSuccess: () => toast({ variant: 'success', title: 'Campaign queued', description: QUEUED_NOTE }),
            onError: (err) =>
              toast({ variant: 'error', title: 'Saved as a draft — not sent', description: (err as Error).message }),
          });
        } else {
          toast({ variant: 'success', title: 'Campaign scheduled' });
        }
      },
      onError: (err) => {
        toast({ variant: 'error', title: 'Failed', description: (err as Error).message });
      },
    });
  }, [campaignName, audienceType, rfmSegment, selectedChannels, templateId, scheduledAt, createMutation, sendMutation, toast]);

  function handleSend() {
    if (!sendTarget) return;
    sendMutation.mutate(sendTarget.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: 'Campaign queued', description: QUEUED_NOTE });
        setSendTarget(null);
      },
      onError: (err) => {
        toast({ variant: 'error', title: 'Not sent', description: (err as Error).message });
        setSendTarget(null);
      },
    });
  }

  function handleDelete() {
    if (!deleteTarget) return;
    deleteMutation.mutate(deleteTarget.id, {
      onSuccess: () => {
        toast({ variant: 'success', title: 'Campaign deleted' });
        setDeleteTarget(null);
      },
      onError: (err) => {
        toast({ variant: 'error', title: 'Delete failed', description: (err as Error).message });
        setDeleteTarget(null);
      },
    });
  }

  const columns: ColumnDef<Row>[] = useMemo(() => [
    {
      key: 'name',
      header: 'Campaign',
      render: (_v, row) => (
        <button
          type="button"
          onClick={() => setSelectedCampaignId(row.id)}
          className="group text-left -my-1 rounded-lg px-2 py-1 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
        >
          <p className="text-sm font-bold text-neutral-900 dark:text-neutral-100 group-hover:text-teal-700 dark:group-hover:text-teal-300">
            {row.name}
          </p>
          <p className="text-[11px] text-neutral-500 dark:text-neutral-400 capitalize">
            {row.audience_type.replace(/_/g, ' ').toLowerCase()}
          </p>
        </button>
      ),
    },
    {
      key: 'channels',
      header: 'Channels',
      render: (_v, row) => (
        <div className="flex flex-wrap gap-1">
          {row.channels.map((ch) => (
            <ChannelPill key={ch} channel={ch} />
          ))}
        </div>
      ),
    },
    {
      key: 'sent_count',
      header: 'Sent',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono">
          {row.sent_count.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'failed_count',
      header: 'Failed',
      numeric: true,
      render: (_v, row) => (
        <span className={`text-sm tabular-nums font-mono ${row.failed_count ? 'font-bold text-danger-600 dark:text-danger-400' : 'text-neutral-700 dark:text-neutral-200'}`}>
          {(row.failed_count ?? 0).toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'delivered_count',
      header: 'Delivered',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums font-mono">
          {row.delivered_count.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'opened_count',
      header: 'Opened',
      numeric: true,
      render: (_v, row) => (
        <span className="text-sm text-neutral-700 dark:text-neutral-200 tabular-nums font-mono">
          {row.opened_count.toLocaleString('en-IN')}
        </span>
      ),
    },
    {
      key: 'status',
      header: 'Status',
      render: (_v, row) => <StatusBadge status={row.status} variant="campaign" />,
    },
    {
      key: 'created_at',
      header: 'Date',
      render: (_v, row) => (
        <span className="text-xs text-neutral-500 dark:text-neutral-400 tabular-nums font-mono">
          {new Date(row.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
        </span>
      ),
    },
    {
      key: 'id',
      header: '',
      width: '88px',
      render: (_v, row) => (
        <div className="flex items-center justify-end gap-1">
          {canSend(row.status) ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setSendTarget(row); }}
              className="rounded-lg p-1.5 text-neutral-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
              aria-label={`Send campaign ${row.name}`}
              title="Send now"
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5" />
              </svg>
            </button>
          ) : null}
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setDeleteTarget(row); }}
            className="rounded-lg p-1.5 text-neutral-400 hover:text-danger-500 hover:bg-danger-50 dark:hover:bg-danger-900/20 transition-colors"
            aria-label={`Delete campaign ${row.name}`}
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
            </svg>
          </button>
        </div>
      ),
    },
  ], [setSelectedCampaignId]);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-500 via-cyan-500 to-emerald-500 p-6 sm:p-7 text-white mb-6 shadow-[0_25px_70px_-25px_rgba(20,184,166,0.7)]">
        <div className="pointer-events-none absolute -top-24 -right-16 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-16 -left-10 h-48 w-48 rounded-full bg-black/15 blur-3xl" />
        <div className="relative flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-[10px] font-bold uppercase tracking-wider ring-1 ring-white/25 backdrop-blur-sm">
              Marketing
            </span>
            <h1 className="mt-3 text-2xl sm:text-3xl font-bold tracking-tight text-white">Campaigns</h1>
            <p className="mt-1.5 text-sm text-white/90 max-w-xl">
              Reach your customers with targeted campaigns across WhatsApp, Email, and SMS.
            </p>
          </div>
          <Button
            ref={createBtnRef}
            variant="primary"
            onClick={() => setShowCreate(true)}
            className="!bg-white !text-teal-700 hover:!bg-white/90 !border-transparent"
          >
            + New Campaign
          </Button>
        </div>
      </div>

      {/* Filter */}
      <div className="rounded-xl bg-white/80 dark:bg-neutral-800/80 backdrop-blur-sm shadow-md p-4 sm:p-5 mb-6">
        <div className="w-full lg:w-44">
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onChange={(v) => setParam('status', typeof v === 'string' ? v : '')}
          />
        </div>
      </div>

      {/* Data */}
      {isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} variant="rect" height={56} className="rounded-lg" />
          ))}
        </div>
      ) : rows.length === 0 && !isFetching ? (
        <div className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg p-10 animate-fade-in">
          <EmptyState
            variant="page"
            title="No campaigns"
            description="Create your first marketing campaign to engage customers across channels."
            action={{ label: '+ Create Campaign', onClick: () => setShowCreate(true) }}
            icon={
              <svg className="h-20 w-20 text-teal-300 dark:text-teal-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M11 5.882V19.24a1.76 1.76 0 01-3.417.592l-2.147-6.15M18 13a3 3 0 100-6M5.436 13.683A4.001 4.001 0 017 6h1.832c4.1 0 7.625-1.234 9.168-3v14c-1.543-1.766-5.067-3-9.168-3H7a3.988 3.988 0 01-1.564-.317z" />
              </svg>
            }
          />
        </div>
      ) : (
        <section className="rounded-2xl bg-white dark:bg-neutral-800 shadow-lg overflow-hidden animate-slide-up" aria-label="Campaigns list">
          <DataTable<Row>
            columns={columns}
            data={rows}
            loading={isFetching}
            totalCount={data?.meta.total}
            currentPage={page}
            pageSize={20}
            onPageChange={(p) => setParam('page', String(p))}
            stickyHeader
          />
        </section>
      )}

      {/* Create Campaign Modal */}
      <Modal open={showCreate} onClose={closeCreate} title="New Campaign" variant="wide">
        <div className="space-y-5">
          <Input
            label="Campaign Name"
            value={campaignName}
            onChange={(v) => setCampaignName(v.slice(0, 200))}
            placeholder="e.g. Diwali Flash Sale"
            required
          />

          <Select
            label="Template"
            options={templateOptions}
            value={templateId}
            onChange={(v) => setTemplateId(typeof v === 'string' ? v : '')}
          />

          <RadioGroup
            label="Audience"
            value={audienceType}
            onChange={(v) => setAudienceType(v as CampaignAudienceType)}
            variant="horizontal"
            options={AUDIENCE_OPTIONS}
          />

          {audienceType === 'RFM_SEGMENT' ? (
            <Select
              label="RFM Segment"
              options={RFM_SEGMENTS}
              value={rfmSegment}
              onChange={(v) => setRfmSegment(typeof v === 'string' ? v : 'CHAMPION')}
            />
          ) : null}

          <div>
            <p className="text-sm font-semibold text-neutral-900 dark:text-neutral-100 mb-2">Channels</p>
            <div className="flex flex-wrap gap-2" role="group" aria-label="Select channels">
              {CHANNEL_OPTIONS.map((ch) => (
                <button
                  key={ch.value}
                  type="button"
                  role="switch"
                  aria-pressed={selectedChannels.includes(ch.value)}
                  aria-label={ch.label}
                  onClick={() => toggleChannel(ch.value)}
                  className={`rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
                    selectedChannels.includes(ch.value)
                      ? 'bg-teal-500 text-white shadow-sm'
                      : 'bg-neutral-100 dark:bg-neutral-700/60 text-neutral-700 dark:text-neutral-200 hover:bg-teal-100 dark:hover:bg-teal-900/30'
                  }`}
                >
                  {ch.label}
                </button>
              ))}
            </div>
          </div>

          <DatePicker
            label="Schedule (optional)"
            value={scheduledAt}
            onChange={setScheduledAt}
            placeholder="Leave empty to send immediately"
            minDate={new Date()}
          />

          <div className="flex items-center justify-end gap-3 pt-2">
            <Button variant="secondary" onClick={closeCreate}>Cancel</Button>
            <Button variant="primary" onClick={handleCreate} loading={createMutation.isPending}>
              {scheduledAt ? 'Schedule Campaign' : 'Create Campaign'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Campaign Detail Drawer */}
      <Drawer
        open={Boolean(selectedCampaignId)}
        onClose={() => setSelectedCampaignId(null)}
        title="Campaign Details"
        position="right"
      >
        {selectedCampaign ? (
          <div className="space-y-5 p-1">
            <div>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Campaign</p>
              <p className="text-lg font-bold text-neutral-900 dark:text-neutral-100">{selectedCampaign.name}</p>
            </div>

            <div className="flex items-center gap-2">
              <StatusBadge status={selectedCampaign.status} variant="campaign" />
              <span className="text-xs text-neutral-500 dark:text-neutral-400 capitalize">
                {selectedCampaign.audience_type.replace(/_/g, ' ').toLowerCase()}
              </span>
            </div>

            <div className="flex flex-wrap gap-1.5">
              {selectedCampaign.channels.map((ch) => (
                <ChannelPill key={ch} channel={ch} size="md" />
              ))}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="rounded-xl bg-gradient-to-br from-brand-50 to-indigo-50 dark:from-brand-900/20 dark:to-indigo-900/20 p-4 text-center shadow-sm">
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono">{selectedCampaign.sent_count.toLocaleString('en-IN')}</p>
                <p className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase mt-1">Sent</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-danger-50 to-warning-50 dark:from-danger-900/20 dark:to-warning-900/20 p-4 text-center shadow-sm">
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono">{(selectedCampaign.failed_count ?? 0).toLocaleString('en-IN')}</p>
                <p className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase mt-1">Failed</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-success-50 to-emerald-50 dark:from-success-900/20 dark:to-emerald-900/20 p-4 text-center shadow-sm">
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono">{selectedCampaign.delivered_count.toLocaleString('en-IN')}</p>
                <p className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase mt-1">Delivered</p>
              </div>
              <div className="rounded-xl bg-gradient-to-br from-violet-50 to-pink-50 dark:from-violet-900/20 dark:to-pink-900/20 p-4 text-center shadow-sm">
                <p className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 tabular-nums font-mono">{selectedCampaign.opened_count.toLocaleString('en-IN')}</p>
                <p className="text-[10px] font-medium text-neutral-500 dark:text-neutral-400 uppercase mt-1">Opened</p>
              </div>
            </div>

            {selectedCampaign.scheduled_at ? (
              <div>
                <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Scheduled</p>
                <p className="text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                  {new Date(selectedCampaign.scheduled_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                </p>
              </div>
            ) : null}

            <div>
              <p className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wider mb-1">Created</p>
              <p className="text-sm font-mono tabular-nums text-neutral-900 dark:text-neutral-100">
                {new Date(selectedCampaign.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
              </p>
            </div>

            {canSend(selectedCampaign.status) ? (
              <div className="pt-2">
                <Button
                  variant="primary"
                  onClick={() => setSendTarget(selectedCampaign)}
                  loading={sendMutation.isPending}
                  className="w-full"
                >
                  Send Now
                </Button>
              </div>
            ) : null}
          </div>
        ) : (
          <div className="space-y-3 p-1">
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} variant="rect" height={40} className="rounded-lg" />
            ))}
          </div>
        )}
      </Drawer>

      {/* Send Confirmation */}
      <ConfirmDialog
        open={Boolean(sendTarget)}
        onCancel={() => setSendTarget(null)}
        onConfirm={handleSend}
        title="Send Campaign"
        description={`Send "${sendTarget?.name}" to your customers now? Each customer in the audience gets one message on ${sendTarget?.channels.join(', ')} (customers who opted out are skipped).`}
        confirmLabel="Send Now"
        variant="warning"
        loading={sendMutation.isPending}
      />

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={handleDelete}
        title="Delete Campaign"
        description={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}

export function CampaignsPage() {
  return (
    <PageContainer
      title="Campaigns"
      breadcrumbs={[
        { label: 'Home', href: '/home' },
        { label: 'Communications', href: '/communications' },
        { label: 'Campaigns' },
      ]}
    >
      <ErrorBoundary resetKey="comm-campaigns">
        <CampaignsContent />
      </ErrorBoundary>
    </PageContainer>
  );
}
